/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useEffect, useState } from 'react';
import { getAppConfigFull, saveAppConfig } from '../../_shared/services/utils.service';
import { loadConfig } from '../../actions/utils';
import { Editor } from '../../_shared/Editor';
import { SettingsForm } from './SettingsForm';
import { HistoryPanel } from './HistoryPanel';
import { validateConfig, diffConfigs, formatValue } from './configHelpers';
import { get, isEqual, cloneDeep } from 'lodash';
import moment from 'moment';
import { Tabs, Button, message, Alert, Spin, Modal, Icon } from 'antd';
import { styles } from './editconfig.styles';

const { TabPane } = Tabs;

export const EditConfig = () => {
    const [serverConfig, setServerConfig] = useState(null);
    const [editedConfig, setEditedConfig] = useState(null);
    const [meta, setMeta] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState('form');
    // Pending save awaiting user confirmation: { config, changes } or null
    const [pendingSave, setPendingSave] = useState(null);
    // Bumped to remount the JSON editor when editedConfig changes outside of it
    const [editorKey, setEditorKey] = useState(0);

    const isDirty = serverConfig && editedConfig && !isEqual(serverConfig, editedConfig);

    const getServerConfig = async () => {
        try {
            const resp = await getAppConfigFull();
            const config = get(resp, 'data', {});
            setServerConfig(cloneDeep(config));
            setEditedConfig(cloneDeep(config));
            setMeta(get(resp, 'meta') || {});
            setLoading(false);
        } catch (err) {
            console.error(err);
            message.error('Failed to load settings');
            setLoading(false);
        }
    };

    // Step 1 of saving: validate, diff against the saved config, and open the
    // confirmation modal listing every change. Used by both the Settings form
    // save button and the Raw JSON editor save button.
    const requestSave = (configToSave) => {
        const errors = validateConfig(configToSave);
        if (errors.length) {
            Modal.error({
                title: 'Some settings are invalid',
                content: (
                    <div>
                        {errors.map(e => (
                            <div key={e.key} style={{ marginBottom: 8, fontSize: 12 }}>
                                <code>{e.key}</code> ({e.label}): {e.error}
                            </div>
                        ))}
                        <div style={{ marginTop: 8, color: '#888', fontSize: 12 }}>
                            Fix the highlighted settings and try again.
                        </div>
                    </div>
                ),
            });
            return;
        }

        const changes = diffConfigs(serverConfig, configToSave);
        if (!changes.length) {
            message.info('No changes to save');
            return;
        }

        setPendingSave({ config: cloneDeep(configToSave), changes });
    };

    // Step 2: user confirmed the diff — actually persist. Sends the
    // lastSaved timestamp this page loaded so the server can reject the save
    // (409) if someone else saved after that — a full-document save from a
    // stale copy would silently revert their changes.
    const confirmSave = async () => {
        if (!pendingSave) return;
        try {
            setSaving(true);
            const resp = await saveAppConfig(pendingSave.config, get(meta, 'lastSaved'));
            setServerConfig(cloneDeep(pendingSave.config));
            setEditedConfig(cloneDeep(pendingSave.config));
            setMeta(get(resp, 'meta') || { lastSaved: new Date().toISOString() });
            setEditorKey(k => k + 1);
            await loadConfig();
            message.success('Settings saved');
            setPendingSave(null);
        } catch (err) {
            if (get(err, 'response.status') === 409) {
                setPendingSave(null);
                Modal.confirm({
                    title: 'Settings changed since you loaded them',
                    content: (
                        <div>
                            <p>{get(err, 'response.data.error', 'Someone else saved the settings after this page loaded them.')}</p>
                            <p style={{ color: '#888', fontSize: 12 }}>
                                Reloading shows the latest saved settings — your unsaved edits here will be
                                discarded, so note them down first if you want to re-apply them.
                            </p>
                        </div>
                    ),
                    okText: 'Reload settings',
                    cancelText: 'Keep editing',
                    onOk: async () => {
                        setLoading(true);
                        await getServerConfig();
                        setEditorKey(k => k + 1);
                    },
                });
                return;
            }
            const resp = get(err, 'response.data.error', err.message);
            message.error(resp);
            console.error(err);
        } finally {
            setSaving(false);
        }
    };

    const handleEditorSave = (val) => {
        setEditedConfig(val);
        requestSave(val);
    };

    const handleEditorChange = (val) => {
        setEditedConfig(val);
    };

    const discardChanges = () => {
        setEditedConfig(cloneDeep(serverConfig));
        setEditorKey(k => k + 1);
    };

    const downloadJson = () => {
        try {
            const blob = new Blob([JSON.stringify(editedConfig, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `gps-settings-${moment().format('YYYY-MM-DD-HHmm')}.json`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (err) {
            message.error('Failed to download settings JSON');
        }
    };

    useEffect(() => {
        getServerConfig();
    }, []);

    useEffect(() => {
        if (!isDirty) return;
        const handler = (e) => {
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [isDirty]);

    if (loading) return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;

    const lastSavedText = meta && meta.lastSaved
        ? `Last saved: ${moment(meta.lastSaved).format('MMM D, YYYY h:mm A')}${meta.savedBy ? ` by ${meta.savedBy}` : ''}`
        : 'Last saved: unknown';

    return (
        <div css={styles.container}>
            {isDirty && (
                <Alert
                    message="You have unsaved changes"
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
            )}

            <Tabs activeKey={activeTab} onChange={setActiveTab}>
                <TabPane tab="Settings" key="form">
                    <div css={styles.tabContent}>
                        <SettingsForm
                            value={editedConfig}
                            onChange={setEditedConfig}
                        />
                    </div>
                </TabPane>
                <TabPane tab="History" key="history">
                    <div css={styles.tabContent}>
                        <HistoryPanel active={activeTab === 'history'} refreshKey={get(meta, 'lastSaved')} />
                    </div>
                </TabPane>
                <TabPane tab="Raw JSON" key="json">
                    <div css={styles.tabContent}>
                        <div css={styles.jsonToolbar}>
                            <Button onClick={downloadJson}>
                                <Icon type="download" /> Download JSON
                            </Button>
                            <Button onClick={discardChanges} disabled={!isDirty}>
                                <Icon type="rollback" /> Reset to Saved
                            </Button>
                            <span className="lastSaved">
                                <Icon type="clock-circle" style={{ marginRight: 4 }} />
                                {lastSavedText}
                            </span>
                        </div>
                        <Editor
                            key={editorKey}
                            value={editedConfig}
                            onChange={handleEditorChange}
                            onSave={handleEditorSave}
                        />
                    </div>
                </TabPane>
            </Tabs>

            {activeTab === 'form' && (
                <div css={styles.saveBar}>
                    <Button
                        type="primary"
                        size="large"
                        onClick={() => requestSave(editedConfig)}
                        loading={saving}
                        disabled={!isDirty}
                    >
                        Save Settings
                    </Button>
                    {isDirty && (
                        <Button
                            size="large"
                            onClick={discardChanges}
                        >
                            Discard Changes
                        </Button>
                    )}
                    <span style={{ color: '#888', fontSize: 12 }}>{lastSavedText}</span>
                </div>
            )}

            <Modal
                visible={!!pendingSave}
                title="Confirm settings changes"
                okText="Save Changes"
                cancelText="Cancel"
                onOk={confirmSave}
                onCancel={() => setPendingSave(null)}
                confirmLoading={saving}
                width={640}
            >
                <div style={{ marginBottom: 12, color: '#555' }}>
                    The following {pendingSave && pendingSave.changes.length > 1 ? `${pendingSave.changes.length} settings` : 'setting'} will be updated:
                </div>
                <div css={styles.diffTable}>
                    {(pendingSave ? pendingSave.changes : []).map(change => (
                        <div className="diffRow" key={change.key}>
                            <code className="diffKey">{change.key}</code>
                            <div className="diffValues">
                                <span className="oldVal">{formatValue(change.oldValue)}</span>
                                <span className="arrow">→</span>
                                <span className="newVal">{formatValue(change.newValue)}</span>
                            </div>
                        </div>
                    ))}
                </div>
            </Modal>
        </div>
    );
};
